import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web/slate/Button";
import { VerticalLine } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import classNames from "classnames";
import * as React from "react";
import styled, { css } from "styled-components";

import { HeaderLinkBase } from "@faire/retailer/components/Header/HeaderLinkBase";
import { AccessibilityLinkCss } from "@faire/retailer/lib/AccessibilityLink";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

import { linkHoverColor } from "./Colors";

export const SMALL_LOGO_WIDTH = 320;

export const NAVIGATION_REDESIGN_MAX_HEADER_WIDTH = 1920;

export const HeaderIconContainer = styled.div`
  width: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 32px;
  }
`;

export const HeaderItemCss = css`
  ${TypographyStyles({ as: undefined })};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 12px;
  cursor: pointer;
  transition: color 0.1s linear;
  white-space: nowrap;

  ${AccessibilityLinkCss}

  &:hover {
    color: ${linkHoverColor};
  }

  ${responsiveMediaQueries.mobile} {
    height: ${() => (isLoggedInRetailer() ? "30px" : "50px")};
    ${() => (isLoggedInRetailer() ? "margin-top: 12px" : undefined)};
  }
`;

export const HeaderLink = styled(HeaderLinkBase)`
  ${HeaderItemCss};
`;

export const HeaderAnchor = styled(Anchor)`
  ${HeaderItemCss};
`;

export const HeaderButton = styled(Button).attrs({ variant: "primary" })`
  height: 36px;
  padding: 0 20px;
  margin: 0 12px;
  white-space: nowrap;
  width: max-content;

  ${responsiveMediaQueries.mobile} {
    ${TypographyStyles({ variant: "labelSansRegular", color: "white" })}
    margin-left: 0;
    margin-right: 12px;
    padding: 0 10px;
    height: 32px;
  }
`;

const getHeaderLinkPadding = (screenWidth?: number) => {
  if (screenWidth == null) {
    return getSpacing(0, "2x");
  } else if (0 <= screenWidth && screenWidth <= SMALL_LOGO_WIDTH) {
    return getSpacing(0, "1x");
  } else {
    return getSpacing(0, "2x");
  }
};

export const HeaderLinkButton = styled.button<{
  $width?: number;
}>`
  ${HeaderItemCss};
  padding: ${({ $width }) => getHeaderLinkPadding($width)};
  position: relative;
`;

export const HeaderLine = styled(VerticalLine).attrs({
  height: "24px",
  color: Core.border.subdued,
  margin: "0 15px",
})``;

export const HeaderDropdown = styled.div<{
  $right?: string;
  $left?: string;
  $width?: string;
  $maxHeight?: string;
  $noBoxShadow?: boolean;
}>`
  position: absolute;
  width: ${(props) => (props.$width ? props.$width : "250px")};
  ${(props) =>
    props.$maxHeight
      ? css`
          max-height: ${props.$maxHeight};
          overflow: auto;
        `
      : ""}
  background: white;
  top: 60px;
  right: ${(props) => (props.$right ? props.$right : "5px")};
  ${(props) =>
    props.$left
      ? css`
          left: ${props.$left};
        `
      : ""};
  box-shadow: ${(props) =>
    props.$noBoxShadow ? "none" : "0 1px 20px rgba(0, 0, 0, 0.1)"};
  z-index: 1;
`;

const HeaderContainerBase = styled.header<{
  $zIndex: number;
  $isMinimal?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${Core.surface.inverse};
  z-index: ${(props) => props.$zIndex};
  border-bottom: 1px solid ${Core.border.muted};
  transition: background 0.1s linear;
  position: relative;
  flex: 0 0 auto;

  &.isAdmin {
    border-bottom: 3px solid blue !important;
    box-sizing: border-box;
  }

  &.impersonating {
    border-bottom: 3px solid lime !important;
    box-sizing: border-box;
  }

  @media print {
    display: none;
  }
`;

interface IHeaderContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  $isMinimal?: boolean;
}

export const HeaderContainer = React.forwardRef<
  HTMLElement,
  IHeaderContainerProps
>((props: IHeaderContainerProps, ref) => {
  const { user } = UserStore.get();

  const className = classNames(
    {
      impersonating: user?.impersonating,
      isAdmin: user?.is_employee,
    },
    props.className
  );

  return (
    <HeaderContainerBase
      {...props}
      className={className}
      $zIndex={301}
      ref={ref}
    >
      {props.children}
    </HeaderContainerBase>
  );
});
